import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useLayoutEffect,
} from "react";
import { useTranslation } from "react-i18next";
import styles from "./PdfReader.module.scss";
import localKeys from "../../../locales/localesKeys.json";
import { getGoogleDriveDirectLink } from "../../../utils";

// ─── icon helpers (inline SVGs — no extra deps) ───────────────────────────────
const Icon = ({ d, size = 20, color = "currentColor", ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
    aria-hidden="true"
    {...rest}
  >
    <path d={d} />
  </svg>
);

const ChevronLeft = (p) => (
  <Icon d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z" {...p} />
);
const ChevronRight = (p) => (
  <Icon d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" {...p} />
);
const ZoomIn = (p) => (
  <Icon
    d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14zm.5-7H9v2H7v1h2v2h1v-2h2V9h-2V7z"
    {...p}
  />
);
const ZoomOut = (p) => (
  <Icon
    d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14zM7 9h5v1H7V9z"
    {...p}
  />
);
const Fullscreen = (p) => (
  <Icon
    d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"
    {...p}
  />
);
const FullscreenExit = (p) => (
  <Icon
    d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"
    {...p}
  />
);
const DownloadIcon = (p) => (
  <Icon d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" {...p} />
);
const OpenNew = (p) => (
  <Icon
    d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"
    {...p}
  />
);
const CloseIcon = (p) => (
  <Icon
    d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"
    {...p}
  />
);
const FitWidth = (p) => (
  <Icon
    d="M3 3h18v2H3zm0 16h18v2H3zm7-3l-4-4 4-4v3h6V9l4 4-4 4v-3H10z"
    {...p}
  />
);
const RotateRight = (p) => (
  <Icon
    d="M15.55 5.55L11 1v3.07C7.06 4.56 4 7.92 4 12s3.05 7.44 7 7.93v-2.02c-2.84-.48-5-2.94-5-5.91s2.16-5.43 5-5.91V10l4.55-4.45zM19.93 11c-.17-1.39-.72-2.73-1.62-3.89l-1.42 1.42c.54.75.88 1.6 1.02 2.47H19.93zm-3.15 5.47l1.42 1.41c1.1-1.1 1.77-2.48 1.96-3.88h-2.02c-.14.87-.48 1.72-1.36 2.47z"
    {...p}
  />
);

const MoreVert = (p) => (
  <Icon
    d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"
    {...p}
  />
);





// ─── Thumbnail component ───────────────────────────────────────────────────────
const Thumbnail = ({ pdfDoc, pageNum, isActive, onClick }) => {
  const canvasRef = useRef(null);
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    if (!pdfDoc || rendered) return;
    let cancelled = false;
    const render = async () => {
      try {
        const page = await pdfDoc.getPage(pageNum);
        const viewport = page.getViewport({ scale: 0.25 });
        const canvas = canvasRef.current;
        if (!canvas || cancelled) return;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({
          canvasContext: canvas.getContext("2d"),
          viewport,
        }).promise;
        if (!cancelled) setRendered(true);
      } catch (_) {}
    };
    render();
    return () => { cancelled = true; };
  }, [pdfDoc, pageNum, rendered]);

  return (
    <button
      className={`${styles.thumbnail} ${isActive ? styles.thumbnailActive : ""}`}
      onClick={onClick}
      aria-label={`Go to page ${pageNum}`}
      title={`Page ${pageNum}`}
    >
      <canvas ref={canvasRef} className={styles.thumbnailCanvas} />
      <span className={styles.thumbnailNum}>{pageNum}</span>
    </button>
  );
};

// ─── Scroll Page Component (Continuous Scroll) ──────────────────────────────────
const PdfScrollPage = ({ pdfDoc, pageNum, scale, rotation, onVisible }) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  // Track whether the page has ever been rendered to avoid clearing it when scrolled away
  const hasRenderedRef = useRef(false);
  const [isVisible, setIsVisible] = useState(false);
  const renderTaskRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setIsVisible(true);
          onVisible(pageNum);
        } else {
          setIsVisible(false);
        }
      },
      {
        root: null,
        rootMargin: "800px 0px 800px 0px", // Larger preload buffer for large PDFs
        threshold: 0.01,
      }
    );

    const currentEl = containerRef.current;
    if (currentEl) {
      observer.observe(currentEl);
    }
    return () => {
      if (currentEl) {
        observer.unobserve(currentEl);
      }
    };
  }, [pageNum, onVisible]);

  useEffect(() => {
    // Re-render when scale/rotation changes even if page is not in viewport yet
    // but only if the page was already visible (in viewport) to avoid rendering all pages
    if (!pdfDoc || !isVisible) return;
    let cancelled = false;

    const render = async () => {
      if (renderTaskRef.current) {
        try { renderTaskRef.current.cancel(); } catch (_) {}
        renderTaskRef.current = null;
      }
      try {
        const page = await pdfDoc.getPage(pageNum);
        const viewport = page.getViewport({ scale, rotation });
        const canvas = canvasRef.current;
        if (!canvas || cancelled) return;

        const ctx = canvas.getContext("2d");
        const devicePixelRatio = window.devicePixelRatio || 1;

        // Only resize canvas if dimensions actually changed — avoids flicker
        const newW = Math.floor(viewport.width * devicePixelRatio);
        const newH = Math.floor(viewport.height * devicePixelRatio);
        if (canvas.width !== newW || canvas.height !== newH) {
          canvas.width = newW;
          canvas.height = newH;
        }
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);

        const task = page.render({ canvasContext: ctx, viewport });
        renderTaskRef.current = task;
        await task.promise;
        if (!cancelled) hasRenderedRef.current = true;
      } catch (err) {
        if (err?.name !== "RenderingCancelledException") {
          console.error("Scroll page render error (page " + pageNum + "):", err);
        }
      }
    };

    render();

    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        try { renderTaskRef.current.cancel(); } catch (_) {}
        renderTaskRef.current = null;
      }
    };
  }, [pdfDoc, pageNum, isVisible, scale, rotation]);

  const defaultHeight = scale * 800;
  // Keep canvas mounted (don't unmount) to preserve rendered content when scrolled away
  // Use visibility instead of display:none so the canvas retains its dimensions
  const canvasVisible = isVisible || hasRenderedRef.current;

  return (
    <div
      ref={containerRef}
      data-page={pageNum}
      className={styles.scrollPage}
    >
      {/* Canvas always mounted; hidden via opacity when not yet rendered */}
      <canvas
        ref={canvasRef}
        className={styles.scrollCanvas}
        style={{
          display: "block",
          visibility: canvasVisible ? "visible" : "hidden",
          minHeight: canvasVisible ? undefined : `${defaultHeight}px`,
        }}
      />
      {!hasRenderedRef.current && !isVisible && (
        <div className={styles.scrollPagePlaceholder} style={{ height: `${defaultHeight}px` }}>
          <span>{t(localKeys.PDF_READER_LOADING_PAGE, { pageNum })}</span>
        </div>
      )}
    </div>
  );
};

// ─── Main PdfReader Component ──────────────────────────────────────────────────
/**
 * PdfReader
 * @param {string}   fileUrl     — URL of the PDF file
 * @param {string}   [title]     — Display title
 * @param {boolean}  [isModal]   — Renders as an overlay modal when true
 * @param {function} [onClose]   — Called when the modal is dismissed
 * @param {function} [onDownload]— Custom download handler
 * @param {string}   [openUrl]   — URL to open in new tab (fallback)
 */
const PdfReader = ({
  fileUrl,
  title = "Document",
  isModal = false,
  onClose,
  onDownload,
  openUrl,
}) => {
  const mainCanvasRef = useRef(null);
  const containerRef = useRef(null);
  const sidebarRef = useRef(null);
  const renderTaskRef = useRef(null);
  const { t } = useTranslation();

  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.0);
  const [rotation, setRotation] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0); // 0-100
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [jumpInput, setJumpInput] = useState("");
  const [rendering, setRendering] = useState(false);
  const [scrollMode, setScrollMode] = useState("page"); // "page" | "scroll"
  const [overflowOpen, setOverflowOpen] = useState(false);

  const touchStartDistRef = useRef(0);
  const touchStartScaleRef = useRef(1.0);
  const isPinchingRef = useRef(false);
  const scaleRef = useRef(1.0);

  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        isPinchingRef.current = true;
        const dist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
        touchStartDistRef.current = dist;
        touchStartScaleRef.current = scaleRef.current;
      }
    };

    const handleTouchMove = (e) => {
      if (isPinchingRef.current && e.touches.length === 2) {
        e.preventDefault();
        const dist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
        const factor = dist / touchStartDistRef.current;
        const newScale = Math.min(
          Math.max(touchStartScaleRef.current * factor, 0.4),
          4.0
        );
        setScale(parseFloat(newScale.toFixed(2)));
      }
    };

    const handleTouchEnd = () => {
      isPinchingRef.current = false;
    };

    el.addEventListener("touchstart", handleTouchStart, { passive: true });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd, { passive: true });
    el.addEventListener("touchcancel", handleTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
      el.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, []);

  const isGoogleDriveLink = fileUrl?.includes("drive.google.com") || fileUrl?.includes("docs.google.com");
  const googleDriveDirectUrl = isGoogleDriveLink ? getGoogleDriveDirectLink(fileUrl) : fileUrl;
  const normalizedFileUrl = isGoogleDriveLink
    ? `${process.env.REACT_APP_PUBLIC_BASE_PATH || ""}/books/proxy?url=${encodeURIComponent(googleDriveDirectUrl)}`
    : fileUrl;
  const normalizedOpenUrl = getGoogleDriveDirectLink(openUrl);
  const isGoogleDrive = false; // Disable iframe viewer and enable full custom PDF.js viewer for Google Drive files

  // Click-away listener for mobile toolbar overflow menu
  useEffect(() => {
    const handleOutsideClick = () => {
      setOverflowOpen(false);
    };
    if (overflowOpen) {
      document.addEventListener("click", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [overflowOpen]);

  useEffect(() => {
    if (isGoogleDrive) {
      setLoading(false);
      setError(null);
      return;
    }
    if (!normalizedFileUrl) return;
    let cancelled = false;
    let loadingTask = null;
    setLoading(true);
    setLoadProgress(0);
    setError(null);
    setPdfDoc(null);
    setTotalPages(0);

    const load = async () => {
      try {
        const pdfjsLib = await import("pdfjs-dist");
        // Load worker reliably from CDN to support subpaths & server deploys
        pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

        loadingTask = pdfjsLib.getDocument({
          url: normalizedFileUrl,
          cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/cmaps/`,
          cMapPacked: true,
          // Point to the CDN directory containing the WASM decoders (for JBIG2/OpenJPEG)
          wasmUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/wasm/`,
          // Enable HTTP range requests so large PDFs load progressively
          // instead of downloading the entire file before rendering.
          rangeChunkSize: 65536, // 64 KB chunks
          disableRange: false,
          disableStream: false,
          // Avoid re-downloading data already fetched
          disableAutoFetch: false,
          // Improve performance for large docs
          isEvalSupported: false,
          useSystemFonts: true,
          // No limit on image sizes (prevents blank pages from large images)
          maxImageSize: -1,
        });

        // Track download progress to show a meaningful progress bar
        loadingTask.onProgress = ({ loaded, total }) => {
          if (!cancelled && total > 0) {
            setLoadProgress(Math.min(99, Math.round((loaded / total) * 100)));
          }
        };

        const doc = await loadingTask.promise;
        if (cancelled) return;
        setPdfDoc(doc);
        setTotalPages(doc.numPages);
        setLoadProgress(100);
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          console.error("PDF load error:", err);
          setError(t(localKeys.PDF_READER_LOAD_FAILED));
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
      if (loadingTask) {
        try { loadingTask.destroy(); } catch (_) {}
      }
    };
  }, [normalizedFileUrl, isGoogleDrive, t]);

  // ── Render Page ──────────────────────────────────────────────────────────
  const renderPage = useCallback(
    async (doc, pageNum, sc, rot) => {
      if (!doc || !mainCanvasRef.current || scrollMode === "scroll") return;
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
      setRendering(true);
      try {
        const page = await doc.getPage(pageNum);
        const viewport = page.getViewport({ scale: sc, rotation: rot });
        const canvas = mainCanvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        const devicePixelRatio = window.devicePixelRatio || 1;
        canvas.width = viewport.width * devicePixelRatio;
        canvas.height = viewport.height * devicePixelRatio;
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);

        const task = page.render({ canvasContext: ctx, viewport });
        renderTaskRef.current = task;
        await task.promise;
      } catch (err) {
        if (err?.name !== "RenderingCancelledException") {
          console.error("Render error", err);
        }
      } finally {
        setRendering(false);
      }
    },
    [scrollMode]
  );

  useEffect(() => {
    if (pdfDoc && scrollMode === "page") {
      renderPage(pdfDoc, currentPage, scale, rotation);
    }
  }, [pdfDoc, currentPage, scale, rotation, renderPage, scrollMode]);

  // ── Fit-to-width auto-scale ───────────────────────────────────────────────
  const fitToWidth = useCallback(async () => {
    if (!pdfDoc || !containerRef.current) return;
    const page = await pdfDoc.getPage(currentPage);
    const naturalViewport = page.getViewport({ scale: 1.0 });
    const containerW = containerRef.current.clientWidth - 40; // padding
    const newScale = Math.max(0.5, containerW / naturalViewport.width);
    setScale(parseFloat(newScale.toFixed(2)));
  }, [pdfDoc, currentPage]);

  // Auto-fit on mount / resize
  useLayoutEffect(() => {
    if (!pdfDoc) return;
    fitToWidth();
    const observer = new ResizeObserver(() => fitToWidth());
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDoc]);

  // ── Scroll to Page Utility ───────────────────────────────────────────────
  const scrollToPage = useCallback((pageNum) => {
    if (!containerRef.current) return;
    const pageEl = containerRef.current.querySelector(`[data-page="${pageNum}"]`);
    if (pageEl) {
      pageEl.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  // Sync scroll position when toggling continuous scroll mode
  useEffect(() => {
    if (scrollMode === "scroll" && currentPage > 1) {
      const timer = setTimeout(() => {
        scrollToPage(currentPage);
      }, 100);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollMode]);

  // ── Page navigation ───────────────────────────────────────────────────────
  const goTo = useCallback((n, triggerScroll = true) => {
    const target = Math.max(1, Math.min(n, totalPages));
    setCurrentPage(target);
    if (triggerScroll && scrollMode === "scroll") {
      scrollToPage(target);
    }
  }, [totalPages, scrollMode, scrollToPage]);

  const prevPage = useCallback(() => goTo(currentPage - 1, true), [currentPage, goTo]);
  const nextPage = useCallback(() => goTo(currentPage + 1, true), [currentPage, goTo]);

  const handlePageVisible = useCallback((pageNum) => {
    setCurrentPage(pageNum);
  }, []);

  // ── Zoom ─────────────────────────────────────────────────────────────────
  const ZOOM_STEP = 0.2;
  const zoomIn = () => setScale((s) => Math.min(parseFloat((s + ZOOM_STEP).toFixed(2)), 4.0));
  const zoomOut = () => setScale((s) => Math.max(parseFloat((s - ZOOM_STEP).toFixed(2)), 0.4));

  // ── Rotation ─────────────────────────────────────────────────────────────
  const rotate = () => setRotation((r) => (r + 90) % 360);

  // ── Fullscreen (modal overlay) ────────────────────────────────────────────
  const toggleFullscreen = () => setFullscreen((f) => !f);

  // ── Keyboard shortcuts ────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") nextPage();
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") prevPage();
      if (e.key === "+" || e.key === "=") zoomIn();
      if (e.key === "-") zoomOut();
      if (e.key === "Escape" && isModal && onClose) onClose();
      if (e.key === "Escape" && fullscreen) setFullscreen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, totalPages, fullscreen]);

  // ── Jump to page ──────────────────────────────────────────────────────────
  const handleJumpSubmit = (e) => {
    e.preventDefault();
    const n = parseInt(jumpInput, 10);
    if (!isNaN(n)) goTo(n, true);
    setJumpInput("");
  };

  // ── Download (pass-through or fallback) ──────────────────────────────────
  const handleDownload = () => {
    if (onDownload) { onDownload(); return; }
    const a = document.createElement("a");
    a.href = normalizedFileUrl;
    a.download = title.replace(/\s+/g, "_") + ".pdf";
    a.click();
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const wrapperClass = [
    styles.pdfReader,
    isModal ? styles.modal : styles.inline,
    fullscreen ? styles.fullscreenOverlay : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={wrapperClass} role="dialog" aria-label="PDF Reader">
      {/* ── Toolbar ── */}
      <div className={styles.toolbar}>
        {/* Left: title + sidebar toggle */}
        <div className={styles.toolbarLeft}>
          {!isGoogleDrive && !isModal && (
            <button
              className={styles.toolBtn}
              onClick={() => setSidebarOpen((o) => !o)}
              title="Toggle page thumbnails"
              aria-label="Toggle sidebar"
            >
              <Icon
                d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"
                size={20}
              />
            </button>
          )}
          <span className={styles.toolbarTitle} title={title}>
            {title}
          </span>
        </div>

        {/* Center: page navigation */}
        {!isGoogleDrive && (
          <div className={styles.toolbarCenter}>
            <button
              className={styles.toolBtn}
              onClick={prevPage}
              disabled={currentPage <= 1}
              title="Previous page (←)"
            >
              <ChevronLeft size={18} />
            </button>

            <form onSubmit={handleJumpSubmit} className={styles.pageForm}>
              <input
                className={styles.pageInput}
                type="number"
                min={1}
                max={totalPages}
                value={jumpInput || currentPage}
                onChange={(e) => setJumpInput(e.target.value)}
                onBlur={() => setJumpInput("")}
                aria-label="Current page"
              />
              <span className={styles.pageTotal}>/ {totalPages}</span>
            </form>

            <button
              className={styles.toolBtn}
              onClick={nextPage}
              disabled={currentPage >= totalPages}
              title="Next page (→)"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}

        {/* Right: zoom, rotate, fullscreen, download, open-link, close */}
        <div className={styles.toolbarRight}>
          {!isGoogleDrive && (
            <>
              <button className={styles.toolBtn} onClick={zoomOut} title="Zoom out (-)">
                <ZoomOut size={18} />
              </button>
              <span className={styles.zoomLabel}>{Math.round(scale * 100)}%</span>
              <button className={styles.toolBtn} onClick={zoomIn} title="Zoom in (+)">
                <ZoomIn size={18} />
              </button>
            </>
          )}

          {/* Desktop inline controls (hidden on mobile) */}
          <div className={styles.desktopControls}>
            {!isGoogleDrive && (
              <>
                <div className={styles.divider} />
                <button className={styles.toolBtn} onClick={fitToWidth} title="Fit to width">
                  <FitWidth size={18} />
                </button>
                <button className={styles.toolBtn} onClick={rotate} title="Rotate">
                  <RotateRight size={18} />
                </button>
                {/* Scroll Mode Toggle */}
                <button
                  className={`${styles.toolBtn} ${scrollMode === "scroll" ? styles.toolBtnActive : ""}`}
                  onClick={() => setScrollMode((m) => (m === "scroll" ? "page" : "scroll"))}
                  title={scrollMode === "scroll" ? "Switch to Page View" : "Switch to Scroll View"}
                >
                  {scrollMode === "scroll" ? (
                    <Icon d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z" size={18} />
                  ) : (
                    <Icon d="M3 5h18v2H3zm0 6h18v2H3zm0 6h18v2H3z" size={18} />
                  )}
                </button>
              </>
            )}
            <div className={styles.divider} />
            <button className={styles.toolBtn} onClick={handleDownload} title="Download PDF">
              <DownloadIcon size={18} />
            </button>
            {normalizedOpenUrl && (
              <a
                className={styles.toolBtn}
                href={normalizedOpenUrl}
                target="_blank"
                rel="noopener noreferrer"
                title="Open in new tab"
              >
                <OpenNew size={18} />
              </a>
            )}
          </div>

          {/* Mobile overflow controls (hidden on desktop) */}
          <div className={styles.mobileControls}>
            <button
              className={styles.toolBtn}
              onClick={(e) => {
                e.stopPropagation();
                setOverflowOpen(!overflowOpen);
              }}
              title="More options"
              aria-label="More options"
            >
              <MoreVert size={18} />
            </button>
            {overflowOpen && (
              <div className={styles.overflowDropdown}>
                {!isGoogleDrive && (
                  <>
                    <button
                      className={styles.dropdownItem}
                      onClick={() => {
                        setScrollMode((m) => (m === "scroll" ? "page" : "scroll"));
                        setOverflowOpen(false);
                      }}
                    >
                      {scrollMode === "scroll"
                        ? t(localKeys.PDF_READER_PAGE_VIEW)
                        : t(localKeys.PDF_READER_SCROLL_VIEW)}
                    </button>
                    <button
                      className={styles.dropdownItem}
                      onClick={() => {
                        fitToWidth();
                        setOverflowOpen(false);
                      }}
                    >
                      {t(localKeys.PDF_READER_FIT_WIDTH)}
                    </button>
                    <button
                      className={styles.dropdownItem}
                      onClick={() => {
                        rotate();
                        setOverflowOpen(false);
                      }}
                    >
                      {t(localKeys.PDF_READER_ROTATE_PAGE)}
                    </button>
                  </>
                )}
                <button
                  className={styles.dropdownItem}
                  onClick={() => {
                    handleDownload();
                    setOverflowOpen(false);
                  }}
                >
                  {t(localKeys.PDF_READER_DOWNLOAD)}
                </button>
                {normalizedOpenUrl && (
                  <a
                    className={styles.dropdownItem}
                    href={normalizedOpenUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setOverflowOpen(false)}
                  >
                    {t(localKeys.PDF_READER_OPEN_TAB)}
                  </a>
                )}
              </div>
            )}
          </div>

          <div className={styles.divider} />
          <button
            className={styles.toolBtn}
            onClick={toggleFullscreen}
            title={fullscreen ? "Exit fullscreen" : "Fullscreen"}
          >
            {fullscreen ? <FullscreenExit size={18} /> : <Fullscreen size={18} />}
          </button>
          {onClose && (
            <button className={`${styles.toolBtn} ${styles.closeBtn}`} onClick={onClose} title="Close (Esc)">
              <CloseIcon size={18} />
            </button>
          )}
        </div>
      </div>

      {/* ── Body ── */}
      <div className={styles.body}>
        {/* Sidebar / Thumbnails */}
        {!isModal && sidebarOpen && (
          <div ref={sidebarRef} className={styles.sidebar}>
            <p className={styles.sidebarHeading}>{t(localKeys.PDF_READER_PAGES)}</p>
            <div className={styles.thumbnailList}>
              {pdfDoc &&
                Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <Thumbnail
                    key={n}
                    pdfDoc={pdfDoc}
                    pageNum={n}
                    isActive={n === currentPage}
                    onClick={() => goTo(n, true)}
                  />
                ))}
            </div>
          </div>
        )}

        {/* Canvas area */}
        <div ref={containerRef} className={styles.canvasArea}>
          {loading && (
            <div className={styles.stateBox}>
              <div className={styles.spinner} />
              <p className={styles.stateText}>{t(localKeys.PDF_READER_LOADING)}</p>
              {loadProgress > 0 && loadProgress < 100 && (
                <div className={styles.loadProgressBar}>
                  <div
                    className={styles.loadProgressFill}
                    style={{ width: `${loadProgress}%` }}
                  />
                  <span className={styles.loadProgressText}>{loadProgress}%</span>
                </div>
              )}
            </div>
          )}

          {error && !loading && (
            <div className={styles.stateBox}>
              <div className={styles.errorIcon}>⚠</div>
              <p className={styles.stateText}>{error}</p>
              {normalizedOpenUrl && (
                <a
                  href={normalizedOpenUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.fallbackLink}
                >
                  {t(localKeys.PDF_READER_OPEN_BROWSER)}
                </a>
              )}
            </div>
          )}

          {!loading && !error && (
            scrollMode === "scroll" ? (
              <div className={styles.scrollContainer}>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <PdfScrollPage
                    key={n}
                    pdfDoc={pdfDoc}
                    pageNum={n}
                    scale={scale}
                    rotation={rotation}
                    onVisible={handlePageVisible}
                  />
                ))}
              </div>
            ) : (
              <div
                className={`${styles.canvasWrapper} ${rendering ? styles.rendering : ""}`}
              >
                <canvas ref={mainCanvasRef} className={styles.mainCanvas} />
              </div>
            )
          )}

          {/* Bottom page nav with range scrubber */}
          {!loading && !error && !isGoogleDrive && (
            <div className={styles.bottomNav}>
              <button
                className={styles.navBtn}
                onClick={prevPage}
                disabled={currentPage <= 1}
              >
                <ChevronLeft size={16} /> {t(localKeys.PDF_READER_PREV)}
              </button>
              
              <input
                type="range"
                min={1}
                max={totalPages}
                value={currentPage}
                onChange={(e) => goTo(parseInt(e.target.value, 10), true)}
                className={styles.pageSlider}
                aria-label="Page range slider"
              />

              <span className={styles.navLabel}>
                {currentPage} / {totalPages}
              </span>
              <button
                className={styles.navBtn}
                onClick={nextPage}
                disabled={currentPage >= totalPages}
              >
                {t(localKeys.PDF_READER_NEXT)} <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Keyboard hint strip */}
      {!loading && !error && !isGoogleDrive && (
        <div className={styles.keyHints}>
          <span>{t(localKeys.PDF_READER_NAVIGATE_HINT)}</span>
          <span>{t(localKeys.PDF_READER_ZOOM_HINT)}</span>
          {isModal && <span>{t(localKeys.PDF_READER_CLOSE_HINT)}</span>}
        </div>
      )}
    </div>
  );
};

export default PdfReader;
